import { alpha, Box, SxProps, Theme, useTheme } from "@mui/material";
import { athenaColors } from "../landing/colors";
import AppBackdrop from "./appBackdrop";
import { glassContentSx } from "../../theme/glassTokens";

interface VideoGlassContainerProps {
  children: React.ReactNode;
  /**
   * "dark"  — deepNavy glass with white/periwinkle text (WelcomeHeroCard)
   * "light" — frosted paper glass, same as the hero left panel (forms)
   */
  variant?: "dark" | "light";
  /** Extra sx applied to the inner glass card */
  glassSx?: SxProps<Theme>;
  /** When true, renders children directly without any visual container */
  inactive?: boolean;
}

export default function VideoGlassContainer({
  children,
  variant = "dark",
  glassSx,
  inactive,
}: VideoGlassContainerProps) {
  const theme = useTheme();
  const isDark = variant === "dark";

  const darkOverrides: SxProps<Theme> = glassContentSx;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: inactive ? "visible" : "hidden",
        borderRadius: inactive ? 0 : 3,
        boxShadow: inactive
          ? "none"
          : {
              xs: `0 2px 4px ${alpha(athenaColors.deepNavy, 0.18)}`,
              xl: `0 4px 16px ${alpha(athenaColors.deepNavy, 0.28)}`,
            },
        ...(!inactive && isDark
          ? {
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 180px)",
            }
          : {}),
      }}
    >
      {/* Solid fallback */}
      {!inactive && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: athenaColors.royalBlue,
            zIndex: 0,
          }}
        />
      )}

      {/* Animated backdrop. A STILL by default: see appBackdrop.tsx for the
          measurements. Flip IN_APP_BACKDROP there to restore the video. */}
      {!inactive && <AppBackdrop />}

      {/* Dark overlay */}
      {!inactive && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(160deg, ${alpha(
              athenaColors.deepNavy,
              0.88
            )} 0%, ${alpha(athenaColors.deepNavy, 0.78)} 50%, ${alpha(
              athenaColors.royalBlue,
              0.72
            )} 100%)`,
            zIndex: 1,
          }}
        />
      )}

      {/* Glass card (or transparent pass-through when inactive) */}
      <Box
        sx={[
          inactive
            ? {}
            : {
                position: "relative",
                zIndex: 2,
                m: 1.5,
                p: { xs: 3, sm: 3.5, md: 4 },
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderRadius: 2.5,
                boxSizing: "border-box",
                ...(isDark
                  ? {
                      background: alpha(athenaColors.deepNavy, 0.55),
                      border: `1px solid ${alpha(athenaColors.white, 0.22)}`,
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                    }
                  : {
                      background: alpha(
                        theme.palette.secondary.main,
                        theme.custom?.containerOpacity?.medium || 0.75
                      ),
                      backdropFilter: "blur(12px)",
                      border: `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
                      color: theme.custom?.contentText?.primary || theme.palette.text.primary,
                    }),
              },
          ...(!inactive && isDark
            ? [darkOverrides as Record<string, unknown>]
            : []),
          ...(!inactive
            ? (Array.isArray(glassSx) ? glassSx : glassSx ? [glassSx] : [])
            : []),
        ]}
      >
        {children}
      </Box>
    </Box>
  );
}
